import { createContext, use, useMemo } from "react";
import type { JSX, ReactNode } from "react";
import { createOnlineStatusStore, engineOnline } from "./onlineStatus";
import type { OnlineStatusStore } from "./onlineStatus";
import type { PlatformServices } from "./PlatformServices";
import { offersPurchasePaths } from "./purchasePaths";

/** What the provider shares: the services and the connectivity derived from them. */
interface PlatformValue {
  readonly services: PlatformServices;
  readonly onlineStatus: OnlineStatusStore;
}

const PlatformContext = createContext<PlatformValue | null>(null);

/** Properties of {@link PlatformProvider}. */
export interface PlatformProviderProps {
  readonly services: PlatformServices;
  readonly children: ReactNode;
}

/**
 * Provides the platform services chosen in the composition root and one connectivity state of the
 * device for every component (F-78).
 *
 * @param props - Component properties.
 * @returns The provider element.
 */
export function PlatformProvider(props: PlatformProviderProps): JSX.Element {
  const { services } = props;
  const value = useMemo<PlatformValue>(
    () => ({ services, onlineStatus: createOnlineStatusStore(services, engineOnline()) }),
    [services],
  );
  const provider = <PlatformContext value={value}>{props.children}</PlatformContext>;

  return provider;
}

/**
 * Returns what the provider shares.
 *
 * @returns The value.
 * @throws Error if no {@link PlatformProvider} is present.
 */
function usePlatformValue(): PlatformValue {
  const value = use(PlatformContext);

  if (value === null) {
    throw new Error("usePlatform must be used inside a PlatformProvider.");
  }

  return value;
}

/**
 * Returns the platform services.
 *
 * @returns The services.
 * @throws Error if no {@link PlatformProvider} is present.
 */
export function usePlatform(): PlatformServices {
  const { services } = usePlatformValue();

  return services;
}

/**
 * Returns the connectivity of the device shared by every component (F-78).
 *
 * @returns The store.
 * @throws Error if no {@link PlatformProvider} is present.
 */
export function useOnlineStatusStore(): OnlineStatusStore {
  const { onlineStatus } = usePlatformValue();

  return onlineStatus;
}

/**
 * Returns whether the application may offer ways to buy on this platform (ADR-0087 decision 1).
 *
 * @returns `false` in the native apps, `true` otherwise.
 * @throws Error if no {@link PlatformProvider} is present.
 */
export function usePurchasePaths(): boolean {
  const platform = usePlatform();
  const offers = offersPurchasePaths(platform);

  return offers;
}
